import { Alert } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../buttons/PrimaryButton';
import { putRestaurantSettings, putCreateRestaurantSettings, uploadRestaurantImage } from '../config/apiRoutes';
import UserContext from '../contexts/UserContext';
import useFetch from '../hooks/useFetch';
import StandardTextInput from '../inputs/StandardTextInput';
import { normalizeString } from '../models/common';
import { UpdateRestaurantSetting, RestaurantSetting } from '../models/RestaurantSetting';
import StandardTextArea from '../inputs/StandardTextArea';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import SaveIcon from '@mui/icons-material/Save';
import SmallButton from '../buttons/SmallButton';
import ResponsiveModal from '../modal/ResponsiveModal';
import { menuEditorRoute, usePathReconstructor } from '../config/pageRoutes';
import ExclusiveStarIcon from '../common/ExclusiveStarIcon';


type Props = {
    isNew?: boolean;
}

const maxFileSizeMb = 10;

const RestaurantSettings = (props: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { selectedRestaurant, setSelectedRestaurantShortName, refresh, hasFeature } = useContext(UserContext);
    const [restaurantName, setRestaurantName] = useState<string>("");
    const [descriptionHu, setDescriptionHu] = useState<string>("");
    const [descriptionEn, setDescriptionEn] = useState<string>("");
    const [reservationLink, setReservationLink] = useState<string>("");
    const { call, errorMessage, loading } = useFetch();
    const [isUrlValid, setIsUrlValid] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const uploader = useFetch(false, true);
    const [imageError, setImageError] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [createdModal, setCreatedModal] = useState(false);
    const { copyParamsFromPath } = usePathReconstructor();
    const [googleAnalytics, setGoogleAnalytics] = useState<string>("");
    const [facebookPixel, setFacebookPixel] = useState<string>("");

    useEffect(() => {
        if (selectedRestaurant && !props.isNew) {
            setRestaurantName(selectedRestaurant.name);
            setDescriptionHu(selectedRestaurant.descriptionHu || "");
            setDescriptionEn(selectedRestaurant.descriptionEn || "");
            setReservationLink(selectedRestaurant.reservationLink || "");
            setImageUrl(selectedRestaurant.image || "");
            setGoogleAnalytics(selectedRestaurant.googleAnalytics || "");
            setFacebookPixel(selectedRestaurant.facebookPixel || "");
        }
    }, [selectedRestaurant]);


    function isSaveDisabled() {
        if (!isUrlValid) {
            return true;
        }
        if (props.isNew) {
            if (restaurantName !== "") {
                return false;
            }
            return true;
        }
        return false;
    }

    useEffect(() => {
        if (reservationLink === "") {
            return setIsUrlValid(true);
        }
        if (!reservationLink.includes("https://reservours.com")) {
            return setIsUrlValid(false);
        }
        return setIsUrlValid(true);
    }, [reservationLink]);

    function handleDescriptionKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter") {
            e.preventDefault();
        }
    }

    function createShortName(name: string) {
        return normalizeString(name)
            .trim()
            .replaceAll(" ", "-")
            .replace(/[^a-zA-Z0-9-]/g, "");
    }

    async function handleSave() {
        if (isSaveDisabled()) return;
        if (props.isNew) {
            handleCreate();
        } else {
            handleUpdate();
        }
    }

    async function handleUpdate() {
        if (!selectedRestaurant) return;
        const uploadedUrl = await handleFileUpload();
        if (!uploadedUrl) return;
        const response = await call<UpdateRestaurantSetting, RestaurantSetting>("PUT", putRestaurantSettings.replace(":restaurantShortName", selectedRestaurant!.restaurantShortName), {
            name: restaurantName,
            descriptionHu,
            descriptionEn,
            reservationLink,
            image: uploadedUrl,
            googleAnalytics: hasFeature("ANALYTICS_INTEGRATION") ? (googleAnalytics || null) : null,
            facebookPixel: hasFeature("ANALYTICS_INTEGRATION") ? (facebookPixel || null) : null,
        });
        if (response.ok && response.data) {
            setShowSuccess(true);
            setShowError(false);
            setSelectedRestaurantShortName(response.data.restaurantShortName);
        } else {
            setShowError(true);
            setShowSuccess(false);
        }
    }

    async function handleCreate() {
        const response = await call<UpdateRestaurantSetting, RestaurantSetting>("PUT", putCreateRestaurantSettings.replace(":restaurantShortName", createShortName(restaurantName)), {
            name: restaurantName,
            descriptionHu,
            descriptionEn,
            reservationLink,
            image: "",
            googleAnalytics: hasFeature("ANALYTICS_INTEGRATION") ? (googleAnalytics || null) : null,
            facebookPixel: hasFeature("ANALYTICS_INTEGRATION") ? (facebookPixel || null) : null,
        });
        if (response.ok) {
            if (response.data) {
                setShowError(false);
                if (imageUrl) {
                    await handleFileUploadForNewRestaurant(response.data.restaurantShortName);
                }
                await refresh();
                setSelectedRestaurantShortName(response.data.restaurantShortName);
                setCreatedModal(true);
            }
        } else {
            setShowSuccess(false);
        }
    }

    async function handleFileUploadForNewRestaurant(restaurantShortName: string) {
        if (selectedFile == null) return imageUrl;
        const sizeInBytes = selectedFile.size / 1024 / 1024;
        if (sizeInBytes > maxFileSizeMb) {
            setImageError(t("gallery.maximum_file_size"))
            return false;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await uploader.call<any, any>("POST", uploadRestaurantImage.replace(":restaurantShortName", restaurantShortName), formData);
        if (response.ok && response.data) {
            await call<UpdateRestaurantSetting, RestaurantSetting>("PUT", putRestaurantSettings.replace(":restaurantShortName", createShortName(restaurantName)), {
                name: restaurantName,
                descriptionHu,
                descriptionEn,
                reservationLink,
                image: response.data.originalUrl,
                googleAnalytics: hasFeature("ANALYTICS_INTEGRATION") ? (googleAnalytics || null) : null,
                facebookPixel: hasFeature("ANALYTICS_INTEGRATION") ? (facebookPixel || null) : null,
            });
            setSelectedFile(null);
            setImageError(null);
            return response.data.originalUrl;
        } else {
            setImageError(t("gallery.upload_failed"));
            return false;
        }
    }

    async function handleImageSelected(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;
        if (e.target.files.length === 0) return;
        const url = await readUrl(e.target.files[0], e);
        setImageUrl(url as string);
        setSelectedFile(e.target.files[0]);
    }

    async function readUrl(file: any, event: any) {
        return new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = e => res(e.target?.result);
            reader.onerror = e => rej(e);
            reader.readAsDataURL(file);
        });
    }

    async function handleFileUpload() {
        if (selectedRestaurant == null) return;
        if (selectedFile == null) return imageUrl;
        if (imageUrl === selectedRestaurant.image) return imageUrl;
        const sizeInBytes = selectedFile.size / 1024 / 1024;
        if (sizeInBytes > maxFileSizeMb) {
            setImageError(t("gallery.maximum_file_size"))
            return false;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);

        const response = await uploader.call<any, any>("POST", uploadRestaurantImage.replace(":restaurantShortName", selectedRestaurant.restaurantShortName), formData);
        if (response.ok && response.data) {
            setImageUrl(response.data.originalUrl);
            setSelectedFile(null);
            setImageError(null);
            return response.data.originalUrl;
        } else {
            return false;
        }
    }

    function handleFileUploadClick() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }
    function handleContinueToMenuClick() {
        window.location.reload();
        window.location.href = copyParamsFromPath(menuEditorRoute.path);
    }

    return (
        <>
            <div className="grid grid-cols-12 gap-3 mt-3">
                <div className="md:col-span-6 col-span-12">
                    <div className="flex flex-col gap-3">
                        <div>
                            <StandardTextInput
                                label={t("common.restaurant_name")}
                                value={restaurantName}
                                onChange={(e) => setRestaurantName(e.target.value)}
                            />
                        </div>
                        <div>
                            <StandardTextArea
                                label={t("common.restaurant_description_hu")}
                                value={descriptionHu}
                                onChange={(e: any) => setDescriptionHu(e.target.value)}
                                style={{ height: "60px" }}
                                onKeyDown={handleDescriptionKeyDown}
                            />
                        </div>
                        <div>
                            <StandardTextArea
                                label={t("common.restaurant_description_en")}
                                value={descriptionEn}
                                onChange={(e: any) => setDescriptionEn(e.target.value)}
                                style={{ height: "60px" }}
                                onKeyDown={handleDescriptionKeyDown}
                            />
                        </div>
                        <div>
                            <StandardTextInput
                                label={t("common.reservation_link")}
                                value={reservationLink}
                                onChange={(e) => setReservationLink(e.target.value)}
                                error={!isUrlValid}
                                helperText={t("common.reservation_link_tooltip")}
                            />
                        </div>
                        <hr />
                        <div>
                            <StandardTextInput
                                label={t("common.google_analytics")}
                                value={googleAnalytics}
                                onChange={(e) => setGoogleAnalytics(e.target.value)}
                                placeholder='XXXXXXXXXX'
                                disabled={!hasFeature("ANALYTICS_INTEGRATION")}
                                endIcon={
                                    <div className="relative w-full h-full">
                                        <ExclusiveStarIcon className="absolute -top-3 -right-5 text-white" />
                                    </div>
                                }
                                title={hasFeature("ANALYTICS_INTEGRATION") ? "" : t("common.analytic_integration_premium_feature")}
                            />
                        </div>
                        <div>
                            <StandardTextInput
                                label={t("common.facebook_pixel")}
                                value={facebookPixel}
                                onChange={(e) => setFacebookPixel(e.target.value)}
                                placeholder="XXXXXXXXXX"
                                disabled={!hasFeature("ANALYTICS_INTEGRATION")}
                                endIcon={
                                    <div className="relative w-full h-full">
                                        <ExclusiveStarIcon className="absolute -top-3 -right-5 text-white" />
                                    </div>
                                }
                                title={hasFeature("ANALYTICS_INTEGRATION") ? "" : t("common.analytic_integration_premium_feature")}
                            />
                        </div>
                        <div className="hidden md:block">
                            <PrimaryButton
                                disabled={isSaveDisabled()}
                                onClick={handleSave}
                                endIcon={<SaveIcon />}
                                fullWidthOnMobile
                            >
                                {t("common.save")}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
                <div className="md:col-span-6 col-span-12 flex flex-col gap-3">
                    <div className=" font-bold text-primary-dark">
                        {t("common.header_image")}
                    </div>
                    {imageUrl && (
                        <div
                            onClick={handleFileUploadClick}
                            className="bg-neutral-deep w-full aspect-[16/9] rounded-[12px] shadow-sm cursor-pointer hover:bg-neutral-light transition-all">
                            <div className="relative">
                                <img
                                    id="header-image"
                                    className="w-full aspect-[16/9] rounded-[12px]"
                                    src={imageUrl}
                                    alt="invalid_image"
                                    title={t("common.header_image")}
                                />
                                <div className="absolute h-full w-full top-0 left-0 bg-primary-dark text-opacity-0 hover:text-opacity-90 bg-opacity-0 hover:bg-opacity-25 rounded-[12px] flex justify-center items-center h-full text-secondary font-bold">
                                    <div className="text-center">
                                        <UploadFileRoundedIcon
                                            style={{ fontSize: "50px" }}
                                        />
                                        <div>
                                            {t("common.upload_image")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {!imageUrl && (
                        <div onClick={handleFileUploadClick} className="bg-neutral-deep w-full aspect-[16/9] rounded-[12px] shadow-sm cursor-pointer border-solid border-[1px] border-primary-light hover:bg-neutral-light transition-all">
                            <div className="flex justify-center items-center h-full text-primary-light font-bold">
                                <div>
                                    <div className="w-full text-center">
                                        <UploadFileRoundedIcon
                                            style={{ fontSize: "50px" }}
                                        />
                                    </div>
                                    <div>
                                        {t("common.select_image")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <div>
                            <SmallButton
                                variant="strike"
                                onClick={handleFileUploadClick}

                                startIcon={<UploadFileRoundedIcon />}
                            >
                                {selectedFile != null && (
                                    <span>
                                        {selectedFile.name}
                                    </span>
                                )}
                                {selectedFile == null && (
                                    <span>
                                        {t("common.select_image")}
                                    </span>
                                )}
                            </SmallButton>
                        </div>
                        {!imageUrl && (
                            <Alert severity='info' variant="filled">
                                {t("common.image_not_set")}
                            </Alert>
                        )}
                    </div>
                    {imageError && (
                        <Alert severity='error' variant="filled">
                            {imageError}
                        </Alert>
                    )}
                    <input
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelected} />
                </div>
            </div>
            <div className="md:hidden block mt-5">
                <PrimaryButton
                    disabled={isSaveDisabled() || uploader.loading || loading}
                    onClick={handleSave}
                    endIcon={<SaveIcon />}
                    fullWidthOnMobile
                >
                    {t("common.save")}
                </PrimaryButton>
            </div>
            <div className="mt-2">
                {showSuccess && (
                    <Alert severity='success' variant='filled'>
                        {t("common.changes_saved")}
                    </Alert>
                )}
                {showError && (
                    <Alert severity='error' variant='filled'>
                        {t("common.internal_server_error")}: {errorMessage}
                    </Alert>
                )}
            </div>
            <ResponsiveModal
                open={createdModal}
                handleClose={() => { }}
                width="500px"
                minWidth="300px"
            >
                <div className="px-2">
                    <div className="text-[18px] text-primary-dark font-bold text-center ">
                        {t("common.restaurant_created")}
                    </div>
                    <div className="mt-2">
                        {t("common.restaurant_created_text")}
                    </div>
                    <div className="mt-4 text-center">
                        <PrimaryButton
                            onClick={handleContinueToMenuClick}
                            fullWidthOnMobile
                            variant="strike"
                        >
                            {t("common.continue_to_menu")}
                        </PrimaryButton>
                    </div>
                </div>
            </ResponsiveModal>
        </>
    )
}

export default RestaurantSettings